import {ProposalField, ProposalFieldJSON} from "work/entities/proposal/proposal";
import TextChange, { TextChangeJSON } from "work/entities/proposal/redlining/_diff/text-change";

export interface RedlineChangeJSON {
  field: ProposalFieldJSON | null;
  isResolved: boolean;
  action: keyof typeof RedlineAction;
  textChanges: TextChangeJSON[];
}

export class RedlineChange {
  field: ProposalField | null;
  isResolved: boolean;
  action: RedlineAction;
  textChanges?: TextChange[]

  constructor(
    field?: ProposalField,
    isResolved?: boolean,
    action?: RedlineAction,
    textChanges?: TextChange[]
  ) {
    this.field = field ?? null;
    this.isResolved = isResolved ?? false;
    this.action = action ?? RedlineAction.Edit;
    this.textChanges = textChanges ?? [];
  }

  public static fromJSON(json: RedlineChangeJSON): RedlineChange {
    const redlineChange = new RedlineChange();
    const field = json.field ? ProposalField.fromJSON(json.field) : null;
    if (field) {
      redlineChange.field = field;
    }
    redlineChange.isResolved = json.isResolved;
    redlineChange.action = RedlineAction.fromJSON(json.action);
    redlineChange.textChanges = json.textChanges.map((change) => TextChange.fromJSON(change));
    return redlineChange;
  }

  public toJSON(): RedlineChangeJSON {
    return {
      field: this.field?.toJSON() ?? null,
      isResolved: this.isResolved,
      action: RedlineAction.getKey(this.action),
      textChanges: this.textChanges?.map(change => change.toJSON()) ?? []
    }
  }
}

export class RedlineAction {
  // The present tense of the action MUST match the readonly property name
  static readonly Accept: RedlineAction = new RedlineAction(
    'Accept',
    'Accepted'
  );
  static readonly Reject: RedlineAction = new RedlineAction(
    'Reject',
    'Rejected'
  );
  static readonly UndoEdit: RedlineAction = new RedlineAction(
    'UndoEdit',
    'Undid edit'
  );
  static readonly UndoRemove: RedlineAction = new RedlineAction(
    'UndoRemove',
    'Undid removal of'
  );
  static readonly UndoAccept: RedlineAction = new RedlineAction(
    'UndoAccept',
    'Undid acceptance of'
  );
  static readonly UndoReject: RedlineAction = new RedlineAction(
    'UndoReject',
    'Undid rejection of'
  );
  static readonly Edit: RedlineAction = new RedlineAction(
    'Edit',
    'Edited'
  );
  static readonly ReEdit: RedlineAction = new RedlineAction(
    'ReEdit',
    'Re-edited'
  );
  static readonly Add: RedlineAction = new RedlineAction(
    'Add',
    'Added'
  );
  static readonly Remove: RedlineAction = new RedlineAction(
    'Remove',
    'Removed'
  );

  private constructor(private readonly presentTense: string, public readonly actionDescription: string) {
  }

  public toString(): string {
    return this.presentTense;
  }

  public static fromJSON(obj: keyof typeof RedlineAction): RedlineAction {
    return RedlineAction[obj] as RedlineAction;
  }

  // Helper to get the static key name for a RedlineAction instance
  public static getKey(action: RedlineAction): keyof typeof RedlineAction {
    for (const key of Object.keys(RedlineAction) as Array<keyof typeof RedlineAction>) {
      if (RedlineAction[key] === action) {
        return key;
      }
    }
    throw new Error("Invalid RedlineAction instance");
  }
}
